import { Alert, Divider, Link, Stack, Typography, useTheme } from '@mui/material';
import SixCodeTextField from 'components/SixCodeTextField/SixCodeTextField';
import useResendVerificationCode from 'hooks/useResendVerificationCode/useResendVerificationCode';
import useVerifyCode from 'hooks/useVerifyCode/useVerifyCode';
import React, { useState } from 'react';
import Loader from 'v2/components/Loader/Loader';
import useSnackbarContext from 'v2/components/Snackbar/useSnackbarContext';
import { useWizardStepContext } from 'v2/components/WizardModal';

export interface Props {
  email?: string | null;
}
const STEP_INDEX = 1;

const VerifyAccountStep: React.FC<React.PropsWithChildren<Props>> = ({ email }) => {
  const theme = useTheme();
  const { showSnackbar } = useSnackbarContext();
  const handleFetchError = (errorMessage: string) => showSnackbar(errorMessage, { severity: 'error' });
  const { setFooterContent, steps, goToNext, activeStep } = useWizardStepContext();

  const {
    verifyCognitoUser,
    loading: verifyCognitoUserLoading,
    error: verifyCognitoUserError
  } = useVerifyCode({ handleFetchError });

  const { resendVerificationCode, loading: resendVerificationCodeLoading } = useResendVerificationCode({
    handleFetchError
  });

  const [code, setCode] = useState<string[]>(Array(6).fill(''));
  const [error, setError] = useState<boolean>(false);

  const onVerifyCognitoUser = async (email: string | undefined | null, code: string) => {
    if (email && !verifyCognitoUserLoading) {
      const { data } = await verifyCognitoUser(email, code);
      if (data?.verifyAccount) {
        goToNext();
      }
      return data;
    }
  };

  const onResendCode = async () => {
    if (email && !resendVerificationCodeLoading) {
      resendVerificationCode(email);
    }
  };

  React.useLayoutEffect(() => {
    setFooterContent(STEP_INDEX, <></>);
  }, []);

  return (
    <Stack flexDirection="column" gap="16px">
      <Typography variant="h4" color="textPrimary">
        Verify administrator account
      </Typography>
      <Divider />
      <Typography variant="body1" color="textPrimary">
        We have send the verification code to {email}.
      </Typography>
      <Typography variant="body1" color="textPrimary">
        Do not close this screen until you have entered the code.
      </Typography>
      <Stack flexDirection="column" gap="16px">
        <Typography variant="h5" color="textPrimary">
          Verification code
        </Typography>
        <Stack flexDirection="row" gap="18px">
          <SixCodeTextField
            code={code}
            setCode={setCode}
            setError={setError}
            triggerWhenCodeIsFull={() => onVerifyCognitoUser(email, code.join(''))}
          />
        </Stack>
        <Stack flexDirection="column">
          <Typography variant="body2" color="textSecondary">
            No email in inbox or spam after a few minutes?
          </Typography>
          <Link id="resend-code-link" onClick={onResendCode} style={{ cursor: 'pointer' }}>
            Resend code
          </Link>
        </Stack>
      </Stack>
      <Stack>
        {verifyCognitoUserLoading && (
          <Stack width="100%" justifyContent="center" alignItems="center" flexDirection="column">
            <Loader size={10} />
            <Typography variant="body2" color="textSecondary">
              Checking code...
            </Typography>
          </Stack>
        )}
        {(error || verifyCognitoUserError) && (
          <Alert
            variant="standard"
            severity="error"
            style={{ backgroundColor: 'transparent', border: `1px solid ${theme.palette.error.main}` }}
            sx={{
              backgroundColor: theme.palette.error.contrastText,
              '& .MuiSvgIcon-root': {
                color: theme.palette.error.main
              },
              '& .MuiAlert-message': {
                color: theme.palette.error.light
              }
            }}
          >
            <Stack flexDirection="column">
              <Typography color={theme.palette.error.light} variant="h6">
                Invalid verification code
              </Typography>
              <Typography variant="body2" color={theme.palette.error.light}>
                Please check your email for the correct code, or click 'Resend Code' to receive a new one.
              </Typography>
            </Stack>
          </Alert>
        )}
      </Stack>
    </Stack>
  );
};

export default VerifyAccountStep;
